import sistema from '../../../sistema.json';

/**
 * O chat da IA dentro do sistema (ADR 0125): o sistema mostra o chat do Smart Builder, que é quem
 * muda o código. O Smart Builder escreve no `sistema.json` onde ele mora e qual é este sistema lá.
 */

export interface SmartBuilderLink {
  url: string;
  systemId: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Onde está o Smart Builder deste sistema, ou null se o sistema não foi feito por ele. */
export function smartBuilderOf(json: unknown): SmartBuilderLink | null {
  const sb = (json as { smart_builder?: { url?: unknown; sistema_id?: unknown } }).smart_builder;
  if (!sb || typeof sb.url !== 'string' || typeof sb.sistema_id !== 'string') return null;
  try {
    const url = new URL(sb.url);
    if (url.protocol !== 'https:' || !UUID_RE.test(sb.sistema_id)) return null;
    return { url: url.href.replace(/\/+$/, ''), systemId: sb.sistema_id };
  } catch {
    return null;
  }
}

export const SMART_BUILDER = smartBuilderOf(sistema);

/** De onde a pessoa abriu o chat. */
export interface ChatContext {
  page: string;
  part?: string;
}

/** O endereço do chat embutido, já com a tela e a parte escolhida. */
export function chatEmbedUrl(link: SmartBuilderLink, context: ChatContext): string {
  const params = new URLSearchParams({ page: context.page.slice(0, 500) });
  if (context.part) params.set('part', context.part.slice(0, 500));
  return `${link.url}/embed/chat/${link.systemId}?${params}`;
}

/**
 * O chat só abre dentro do sistema quando os dois estão no mesmo site (o login do Smart Builder
 * vale no quadro). Em outro domínio, ele abre numa aba.
 */
export function canEmbed(systemHost: string, link: SmartBuilderLink): boolean {
  const site = (host: string) => host.split('.').slice(-2).join('.');
  return site(systemHost) === site(new URL(link.url).hostname);
}

/** A parte da tela escolhida, em palavras: o texto dela e o título mais perto. */
export function partOf(el: Element): string {
  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 200);
  const section = el.closest('section, form, table, [role="dialog"]');
  const heading = section?.querySelector('h1, h2, h3')?.textContent?.replace(/\s+/g, ' ').trim();
  return heading && heading !== text ? `${heading} › ${text}` : text;
}
